// src/components/postanalysis components/tabs/PA_POCLevelAnalysis.js
import React, { useState, useEffect, useMemo } from "react";
import { useDetailedInsightsData } from "@/hooks/post-analysis/useDetailedInsightsData";
import { formatValue } from "@/lib/post_analysis_utils/formatValue";
import { fetchMetricOptions } from "@/services/api";
import { exportToCSV } from "@/lib/post_analysis_utils/exportToCSV";
import { useGetSummaryData } from "@/hooks/post-analysis/useGetSummaryData";

import { POC_LEVEL_ANALYSIS_COLUMNS } from "@/components/postanalysis components/tables/table_columns";

import UnifiedTable from "@/components/postanalysis components/tables/UnifiedTable";
import MetricCardGroup from "../ui/MetricCardGroup";
import { usePostAnalysisFilters } from "@/hooks/post-analysis/usePostAnalysisFilters";
import PostAnalysisHeaderWithFilters from "@/components/postanalysis components/ui/PostAnalysisHeaderWithFilters";

// ==================== Main Component ====================
export default function POCLevelAnalysis({ backendData = [], filters = { brand: "", region: "", campaign: "", start_date: "", end_date: "", poc_id: [], key_account: "" }, setFilters }) {
  const [showFilters, setShowFilters] = useState(false);
  const [metricOptions, setMetricOptions] = useState({ y_axis: [] });
  const [pocMetricAxis, setPocMetricAxis] = useState('impressions');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Fetch metric options from backend on mount
  useEffect(() => {
    async function fetchOptions() {
      const options = await fetchMetricOptions("detailed_insights");
      setMetricOptions(options);
    }
    fetchOptions();
  }, []);

  // Fetch data for POC level
  const pocData = useDetailedInsightsData(filters, pocMetricAxis);

  // Fetch summary data for campaign count
  const { summaryMetrics } = useGetSummaryData(
    filters.region,
    filters.brand,
    filters.campaign,
    filters.start_date,
    filters.end_date,
    filters.poc_id,
    filters.poc_name,
    filters.key_account,
    "summary"
  );

  // Shared local filter options + change handler, seeded from pocData.filterOptions
  const { localFilterOptions, handleLocalFiltersChange } = usePostAnalysisFilters(
    pocData.filterOptions
  );

  // Calculate summary metrics from table data
  const summaryMetricsCalculated = useMemo(() => {
    const tableData = pocData.pocLevel?.table_data || [];
    const campaignLevelData = pocData.campaignLevel?.table_data || [];
    
    if (!tableData || tableData.length === 0) {
      return {
        totalCampaigns: summaryMetrics?.num_campaigns || 0,
        totalPOCs: 0,
        totalImpressions: 0,
        totalPostEngagements: 0,
        totalReach: 0,
        avgCPM: summaryMetrics?.average_cpm || 0,
        avgEngagementRate: summaryMetrics?.average_er || 0
      };
    }

    // Count unique POCs (since a POC can appear multiple times if in multiple campaigns)
    const uniquePocIds = new Set(tableData.map(row => row.poc_id).filter(id => id));
    const totalPOCs = uniquePocIds.size;
    const totalCampaigns = campaignLevelData.length || summaryMetrics?.num_campaigns || 0;
    const totalImpressions = tableData.reduce((sum, row) => sum + (parseFloat(row.impressions) || 0), 0);
    const totalPostEngagements = tableData.reduce((sum, row) => sum + (parseFloat(row.post_engagement) || 0), 0);
    const totalReach = tableData.reduce((sum, row) => sum + (parseFloat(row.reach) || 0), 0);
    
    // Use CPM from summaryMetrics (calculated from raw data, same as Summary tab)
    // This ensures consistency - Summary tab uses df['cpm'].mean() on all CampaignResult rows
    const avgCPM = summaryMetrics?.average_cpm || 0;
    
    // Use engagement rate from summaryMetrics (calculated from raw data, same as Summary tab)
    const avgEngagementRate = summaryMetrics?.average_er || 0;

    return {
      totalCampaigns: totalCampaigns,
      totalPOCs: totalPOCs,
      totalImpressions: totalImpressions,
      totalPostEngagements: totalPostEngagements,
      totalReach: totalReach,
      avgCPM: avgCPM,
      avgEngagementRate: avgEngagementRate
    };
  }, [pocData.pocLevel?.table_data, pocData.campaignLevel?.table_data, summaryMetrics]);

  // Prepare summary metric cards
  const summaryCards = useMemo(() => [
    {
      title: "Campaigns",
      value: formatValue(summaryMetricsCalculated.totalCampaigns, { type: "pocs" })
    },
    {
      title: "POCs",
      value: formatValue(summaryMetricsCalculated.totalPOCs, { type: "pocs" })
    },
    {
      title: "Total Impressions",
      value: formatValue(summaryMetricsCalculated.totalImpressions, { type: "number" })
    },
    {
      title: "Total Engagements",
      value: formatValue(summaryMetricsCalculated.totalPostEngagements, { type: "number" })
    },
    {
      title: "Total Reach",
      value: formatValue(summaryMetricsCalculated.totalReach, { type: "number" })
    },
    {
      title: "Avg. CPM",
      value: formatValue(summaryMetricsCalculated.avgCPM, { type: "currency" })
    },
    {
      title: "Average Engagement Rate",
      value: formatValue(summaryMetricsCalculated.avgEngagementRate, { type: "percent" })
    }
  ], [summaryMetricsCalculated]);

  // Helper function to get metric type
  const getMetricType = (metricKey) => {
    const metric = metricOptions.y_axis.find(opt => opt.value === metricKey);
    return metric?.type || 'number';
  };

  // Helper to get label from value
  const getLabelFromValue = (value) => metricOptions.y_axis.find(opt => opt.value === value)?.label || value;
  // Helper to get value from label
  const getValueFromLabel = (label) => metricOptions.y_axis.find(opt => opt.label === label)?.value || label;

  // Filter out revenue and volume uplift from metric options
  const filteredYAxisOptions = (metricOptions?.y_axis || []).filter(opt => opt.value !== 'revenue' && opt.value !== 'volume_uplift');

  // yAxisOptions for dropdowns
  const yAxisOptions = filteredYAxisOptions.map(opt => opt.label);

  // Export handler for the table
  const handleExportPocCSV = () => {
    exportToCSV(pocData.pocLevel?.table_data || [], "poc_level_analysis.csv");
  };

  // Calculate pagination
  const allTableData = pocData.pocLevel?.table_data || [];
  const totalRows = allTableData.length;
  const totalPages = Math.ceil(totalRows / pageSize);
  const startIdx = (page - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const paginatedData = allTableData.slice(startIdx, endIdx);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [filters.brand, filters.region, filters.campaign, filters.start_date, filters.end_date, filters.poc_id, filters.key_account]);

  // Debug: Log data to see what we're getting
  useEffect(() => {
    // console.log("POC Level Analysis - pocData:", pocData);
    // console.log("POC Level Analysis - table_data:", pocData.pocLevel?.table_data);
  }, [pocData]);

  return (
    <div className="p-4 space-y-8">
      <PostAnalysisHeaderWithFilters
        title="POC Level Analysis"
        filters={filters}
        setFilters={setFilters}
        baseFilterOptions={pocData.filterOptions}
        localFilterOptions={localFilterOptions}
        handleLocalFiltersChange={handleLocalFiltersChange}
      />

      {/* Summary Metrics */}
      {!pocData.isLoading && !pocData.error && (
        <div className="mb-6">
          <MetricCardGroup metrics={summaryCards} columns={7} />
        </div>
      )}

      {/* POC Level Table */}
      <div className="w-full">
        {pocData.isLoading ? (
          <div className="text-center py-8">Loading POC Level Analysis data...</div>
        ) : pocData.error ? (
          <div className="text-center py-8 text-red-500">
            Error loading data: {pocData.error?.message || "Unknown error"}
          </div>
        ) : (
          <UnifiedTable 
            columns={POC_LEVEL_ANALYSIS_COLUMNS} 
            data={paginatedData} 
            idKey="poc_id" 
            onExportCSV={handleExportPocCSV}
            title="POC Level Analysis"
            isLoading={pocData.isLoading}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalPages={totalPages}
          />
        )}
      </div>
    </div>
  )
}

